// The validator: what a generated game.js must survive before it is kept. Static scan first
// (cheap and exact, so the model never burns a repair round on Math.random), then load it as a
// real module, soak it with bots, check determinism, and check the spec's promises: the score
// field exists, the sprites it asked for resolve. Findings are machine-readable and fed back to
// the model verbatim.
//
// Isomorphic: the loader is injected, so Node (validate.node.ts, a scratch file) and the
// browser (runtime/client/genload.ts, a blob URL) run the same checks.

import type { GameModule } from "../contract.js";
import { searchSprites } from "../shared/sprites.js";
import { spriteIndex } from "../shared/sprite-index.js";
import { ClientView } from "../server/room.js";
import { soak } from "./soak.js";
import type { Spec } from "./spec.js";

export interface Finding {
  level: "error" | "warn";
  /** Stable code: "forbidden", "import", "export", "size", "module-state", "load", "visible", "soak", "determinism", "budget", "layer", "score", "spec", "sprite". */
  code: string;
  message: string;
}

export interface ValidationReport {
  ok: boolean;
  findings: Finding[];
  /** Measured when the soak ran. */
  measured?: { stepAvgMs: number; stepMaxMs: number; stateBytes: number; snapshotBytes: number; hash: string };
}

export type ModuleLoader = (source: string) => Promise<GameModule>;

const FORBIDDEN: [RegExp, string][] = [
  [/\bMath\.random\b/, "Math.random: use rng.next(), rng.int(n) or rng.range(a, b)"],
  [/\bDate\.now\b|\bnew Date\b/, "Date: use world.time and dt"],
  [/\bperformance\.now\b/, "performance.now: use world.time and dt"],
  [/\bsetTimeout\b|\bsetInterval\b|\brequestAnimationFrame\b/, "timers: count a field down by dt instead"],
  [/\bfetch\s*\(|\bXMLHttpRequest\b|\bWebSocket\b/, "network: a module never talks to the network"],
  [/\bwindow\b|\bdocument\b|\blocalStorage\b|\bsessionStorage\b|\bnavigator\b|\bglobalThis\b/, "browser globals: a module has no DOM"],
  [/\beval\s*\(|\bnew Function\b/, "eval: not allowed"],
  [/\brequire\s*\(/, "require: the only import is defineEntity from #runtime"],
];

/** Blank out comments, keeping every character position (and so every line number) the same. */
export function stripComments(source: string): string {
  return source.replace(/\/\*[\s\S]*?\*\/|\/\/[^\n]*/g, (m) => m.replace(/[^\n]/g, " "));
}

/** Everything that can be checked without running the code. */
export function staticScan(raw: string): Finding[] {
  const out: Finding[] = [];
  const source = stripComments(raw);
  if (raw.length > 96 * 1024) out.push({ level: "error", code: "size", message: `game.js is ${raw.length} bytes; keep it under 96 KB` });
  for (const [re, why] of FORBIDDEN) {
    const m = re.exec(source);
    if (m) out.push({ level: "error", code: "forbidden", message: `${why} (found "${m[0]}" at line ${source.slice(0, m.index).split("\n").length})` });
  }
  for (const m of source.matchAll(/^\s*import\s+[^;]*?from\s+["']([^"']+)["']/gm)) {
    if (m[1] !== "#runtime") out.push({ level: "error", code: "import", message: `import from "${m[1]}": the only import is defineEntity from "#runtime"` });
  }
  for (const name of ["meta", "inputSchema"]) {
    if (!new RegExp(`export\\s+const\\s+${name}\\b`).test(source)) out.push({ level: "error", code: "export", message: `missing \`export const ${name}\`` });
  }
  for (const name of ["init", "onJoin", "onLeave"]) {
    if (!new RegExp(`export\\s+function\\s+${name}\\b`).test(source)) out.push({ level: "error", code: "export", message: `missing \`export function ${name}\`` });
  }
  if (!/defineEntity\s*\(/.test(source)) out.push({ level: "error", code: "export", message: "no defineEntity call: define at least the player entity" });
  // Module-scope state is a determinism bug waiting to happen; constants are fine.
  for (const m of source.matchAll(/^(?:let|var)\s+(\w+)\s*=/gm)) {
    out.push({ level: "warn", code: "module-state", message: `module-scope \`let ${m[1]}\`: state must live on entities, not in the file` });
  }
  return out;
}

/** The whole gate, with the module loader of the host it runs on. */
export async function validateSource(source: string, spec: Spec | null, load: ModuleLoader): Promise<ValidationReport> {
  const findings = staticScan(source);
  if (findings.some((f) => f.level === "error")) return { ok: false, findings };

  let mod: GameModule;
  try {
    mod = await load(source);
  } catch (err) {
    findings.push({ level: "error", code: "load", message: `the module failed to load: ${(err as Error).message}` });
    return { ok: false, findings };
  }
  if (![...mod.entities.values()].some((d) => typeof d.draw === "function")) {
    findings.push({ level: "error", code: "visible", message: "no entity has a draw function: nothing would be visible" });
  }

  let measured: ValidationReport["measured"];
  try {
    // Watch play for things players walk across: a dynamic type bigger than the player that a
    // player's centre sits inside for a good share of the time (plots, floors). Drawn on layer
    // 0 it paints over the player, since bigger draws later within a layer. Static types are
    // already drawn underneath by the runtime; solid obstacles never score, because players
    // are pushed out of them.
    const walkedOver = new Map<string, { inside: number; maxR: number }>();
    let playerTicks = 0;
    const a = soak(mod, 3, {
      onTick(room, tick) {
        if (tick % 5) return;
        const players = room.players().map((p) => room.playerEntity(p)).filter((e): e is NonNullable<typeof e> => !!e);
        if (players.length === 0) return;
        playerTicks += players.length;
        const minR = Math.min(...players.map((p) => p.r)) * 1.2;
        for (const e of room.liveEntities()) {
          if (e.owner !== undefined || e.r < minR) continue;
          const def = mod.entities.get(e.type);
          if (!def?.draw || def.static || (def.layer ?? 0) !== 0) continue;
          for (const p of players) {
            if (e.type === p.type) continue;
            if ((p.x - e.x) ** 2 + (p.y - e.y) ** 2 <= e.r * e.r) {
              const t = walkedOver.get(e.type) ?? { inside: 0, maxR: 0 };
              t.inside++;
              if (e.r > t.maxR) t.maxR = e.r;
              walkedOver.set(e.type, t);
            }
          }
        }
      },
    });
    const b = soak(mod, 3);
    const view = new ClientView();
    const snapshotBytes = JSON.stringify(a.room.snapshot("p0", view, 1000)).length;
    measured = { stepAvgMs: a.stepAvgMs, stepMaxMs: a.stepMaxMs, stateBytes: a.stateBytes, snapshotBytes, hash: a.hash };
    if (a.hash !== b.hash) findings.push({ level: "error", code: "determinism", message: "two runs with the same seed and inputs ended in different states: something reads Math.random, Date, or module-scope state" });
    if (a.stepAvgMs > 2) findings.push({ level: "error", code: "budget", message: `a tick costs ${a.stepAvgMs.toFixed(2)} ms on average with 8 players; the budget is 2 ms (fewer entities, cheaper queries)` });
    if (snapshotBytes > 64 * 1024) findings.push({ level: "error", code: "budget", message: `one client's snapshot is ${snapshotBytes} bytes; keep it under 64 KB (fewer replicated fields, static scenery)` });
    for (const [type, t] of walkedOver) {
      const share = playerTicks ? t.inside / playerTicks : 0;
      if (share >= 0.05) {
        findings.push({
          level: "error",
          code: "layer",
          message: `players spend ${Math.round(share * 100)}% of their time inside "${type}" entities (r up to ${Math.round(t.maxR)}), which are bigger than the player and on layer 0, so they draw over the player; give "${type}" layer: -1 (ground) or layer: 1 if it is meant to cover players`,
        });
      }
    }
    if (spec) {
      if (spec.score.field) {
        // Whoever is still in the room after the churn.
        const pid = a.room.players()[0];
        const p = pid ? a.room.playerEntity(pid) : undefined;
        const v = p ? p[spec.score.field] : undefined;
        if (typeof v !== "number") findings.push({ level: "error", code: "score", message: `the spec names "${spec.score.field}" as the score, but the player entity has no numeric field by that name` });
        if (mod.meta.score !== spec.score.field) findings.push({ level: "error", code: "score", message: `meta.score must be "${spec.score.field}" as in the spec` });
      }
      for (const e of spec.entities) {
        if (!mod.entities.has(e.type)) findings.push({ level: "warn", code: "spec", message: `the spec lists an entity type "${e.type}" the module does not define` });
        if (e.sprite && spec.look.style !== "shapes") {
          const hits = searchSprites(spriteIndex, e.sprite, { style: spec.look.style, limit: 1 });
          if (hits.length === 0) {
            const any = searchSprites(spriteIndex, e.sprite, { limit: 3 }).map((s) => `${s.pack}/${s.name}`);
            findings.push({ level: "warn", code: "sprite", message: `no sprite answers "${e.sprite}" in style ${spec.look.style}${any.length ? ` (closest in other styles: ${any.join(", ")})` : ""}; the shape fallback will show` });
          }
        }
      }
    }
    // Every sprite keyword used in the code must resolve in the game's style.
    if (spec && spec.look.style !== "shapes") {
      for (const m of source.matchAll(/world\.sprite\(\s*["']([^"']+)["']/g)) {
        if (searchSprites(spriteIndex, m[1], { style: spec.look.style, limit: 1 }).length === 0) {
          findings.push({ level: "warn", code: "sprite", message: `world.sprite("${m[1]}") finds nothing in style ${spec.look.style}; the shape fallback will show` });
        }
      }
    }
  } catch (err) {
    findings.push({ level: "error", code: "soak", message: `the soak failed: ${(err as Error).message}` });
  }
  return { ok: !findings.some((f) => f.level === "error"), findings, measured };
}
